"use client";
import { useState, useMemo } from "react";
import { metaDescriptions } from "@/client/components/shared";
import Link from "next/link";
import {
  GraduationCap,
  BookOpen,
  Award,
  Search,
  Clock,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { programmes } from "@/data/programmes";
import { categories } from "./categories";
import Image from "next/image";

const iconMap = {
  GraduationCap,
  BookOpen,
  Award,
};

const categoryColors = {
  doctorate: {
    bg: "bg-healing-800",
    bgLight: "bg-healing-50",
    border: "border-healing-200",
    accent: "text-healing-400",
    badge: "bg-healing-500/10 text-healing-800",
    glow: "shadow-healing-500/20",
  },
  diploma: {
    bg: "bg-wisdom-800",
    bgLight: "bg-wisdom-50",
    border: "border-wisdom-200",
    accent: "text-wisdom-400",
    badge: "bg-wisdom-500/10 text-wisdom-800",
    glow: "shadow-wisdom-500/20",
  },
  certificate: {
    bg: "bg-nature-800",
    bgLight: "bg-nature-50",
    border: "border-nature-200",
    accent: "text-nature-400",
    badge: "bg-nature-500/10 text-nature-800",
    glow: "shadow-nature-500/20",
  },
};
export const ProgrammesDirectory = () => {
  const [search, setSearch] = useState("");
  const [durationFilter, setDurationFilter] = useState("all");

  const filtered = useMemo(() => {
    return programmes.filter((p) => {
      const matchesSearch = p.title
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesDuration =
        durationFilter === "all" ||
        (durationFilter === "short" &&
          p.overview.duration.length <= 6 &&
          p.overview.duration.unit === "months") ||
        (durationFilter === "1-2" &&
          p.overview.duration.length <= 2 &&
          p.overview.duration.unit === "years") ||
        (durationFilter === "3+" &&
          p.overview.duration.length >= 3 &&
          p.overview.duration.unit === "years");
      return matchesSearch && matchesDuration;
    });
  }, [search, durationFilter]);

  return (
    <div className="min-h-screen bg-background mt-20 animate-fade-up">
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-healing-900 via-healing-800 to-healing-700" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_hsla(142,71%,45%,0.15),transparent_70%)]" />
        <div className="relative container mx-auto px-4 max-w-6xl text-center">
          <div className="animate-[fadeUp_0.5s_ease-out_both]">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-healing-500/10 text-healing-300 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" /> Explore Our Programmes
            </span>
            <h1 className="font-serif-display text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              Your Journey in
              <br />
              <span className="text-gold-gradient">Natural Medicine</span>{" "}
              Starts Here
            </h1>
            <p className="text-healing-200/80 text-lg max-w-2xl mx-auto">
              From short certificate courses to professional doctorates — find
              the programme that transforms your passion into practice.
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="relative -mt-8 z-10">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-card rounded-2xl shadow-xl border p-4 md:p-6 flex flex-col sm:flex-row gap-3 animate-[fadeUp_0.5s_ease-out_0.2s_both]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search programmes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-muted/50 border-0"
              />
            </div>
            <Select value={durationFilter} onValueChange={setDurationFilter}>
              <SelectTrigger
                aria-label="Filter by duration"
                className="w-full sm:w-48 bg-muted/50 border-0"
              >
                <Clock
                  aria-hidden="true"
                  className="w-4 h-4 mr-2 text-muted-foreground"
                />
                <SelectValue placeholder="Filter by duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Durations</SelectItem>
                <SelectItem value="short">Under 1 Year</SelectItem>
                <SelectItem value="1-2">1–2 Years</SelectItem>
                <SelectItem value="3+">3+ Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            {categories.map((cat, index) => {
              const Icon = iconMap[cat.icon as keyof typeof iconMap];
              const colors = categoryColors[cat.id];
              return (
                <div
                  key={cat.id}
                  className="animate-[fadeUp_0.5s_ease-out_both]"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <Link
                    href={`/programmes/${cat.id}`}
                    className="no-underline text-inherit"
                  >
                    <div
                      className={`group relative rounded-2xl overflow-hidden ${colors.bg} p-8 text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl ${colors.glow} cursor-pointer`}
                    >
                      <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                      <div className="relative">
                        <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                          <Icon className="w-7 h-7" />
                        </div>
                        <h3 className="font-serif-display text-2xl font-bold mb-2">
                          {cat.title}
                        </h3>
                        <p className="text-white/90 text-sm mb-4">
                          {cat.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium bg-white/10 px-3 py-1 rounded-full">
                            {cat.count} Programme{cat.count !== 1 ? "s" : ""}
                          </span>
                          <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Programmes Grid */}
          <div className="mb-8">
            <h2 className="font-serif-display text-2xl md:text-3xl font-bold text-foreground mb-2">
              {search || durationFilter !== "all"
                ? "Search Results"
                : "All Programmes"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {filtered.length} programme{filtered.length !== 1 ? "s" : ""}{" "}
              found
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((prog, index) => {
              const colors = categoryColors[prog.category];
              return (
                <div
                  key={prog.slug}
                  className="animate-[fadeUp_0.5s_ease-out_both]"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Link
                    href={`/programmes/${prog.category}/${prog.slug}`}
                    className="no-underline text-inherit"
                  >
                    <div
                      className={`group rounded-xl border ${colors.border} bg-card overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
                    >
                      <div className="aspect-[16/9] overflow-hidden bg-muted">
                        <Image
                          src={prog.image}
                          width={300}
                          height={300}
                          alt={prog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <span
                          className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge} mb-3`}
                        >
                          {prog.category.charAt(0).toUpperCase() +
                            prog.category.slice(1)}
                        </span>
                        <h3 className="font-serif-display text-lg font-bold text-foreground mb-2 leading-snug group-hover:text-healing-600 transition-colors">
                          {prog.title}
                        </h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
                          <Clock className="w-3 h-3" />
                          {prog.overview.duration.length}{" "}
                          {prog.overview.duration.unit}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {prog.overview.description}
                        </p>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-healing-700 group-hover:underline">
                          View Details{" "}
                          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">
                No programmes match your search. Try adjusting your filters.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
